import React, { useEffect, useState, useCallback } from "react";
import { useCompany } from "../../context/CompanyContext";
import {
  getLatestTaxComputations,
  getTaxPeriods,
  runTaxAction,
} from "../../Services/taxService";
import "./tax.css";

export default function CITCenter() {
  const { companyId } = useCompany();
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;

    try {
      const [periodData, compData] = await Promise.all([
        getTaxPeriods(companyId, "CIT"),
        getLatestTaxComputations(companyId, "CIT"),
      ]);

      setPeriods(periodData || []);
      setRows(compData || []);

      setSelectedPeriod(
        (prev) => prev || (periodData?.length ? periodData[0].id : null)
      );
    } catch (err) {
      setMsg(err.message || "Failed to load CIT data");
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleRun() {
    if (!companyId || !selectedPeriod) return;
    setBusy(true);
    setMsg("");
    try {
      await runTaxAction({
        action: "run_cit",
        companyId,
        taxPeriodId: selectedPeriod,
      });
      setMsg("CIT computation completed");
      await load();
    } catch (err) {
      setMsg(err.message || "Failed to run CIT computation");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="tax-page">
      <div className="tax-page-header">
        <div>
          <h1>CIT Center</h1>
          <p>
            Compute Company Income Tax using the correct small company threshold
            logic.
          </p>
        </div>

        <div className="tax-actions">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            <option value="">Select Period</option>
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.period_start} to {p.period_end}
              </option>
            ))}
          </select>

          <button
            className="tax-btn primary"
            onClick={handleRun}
            disabled={busy || !selectedPeriod}
          >
            {busy ? "Running..." : "Run CIT"}
          </button>
        </div>
      </div>

      {msg ? <div className="tax-alert">{msg}</div> : null}

      <div className="tax-panel">
        <h2>CIT Computations</h2>

        {rows.length === 0 ? (
          <div className="tax-empty">No CIT computations yet.</div>
        ) : (
          <div className="tax-table-wrap">
            <table className="tax-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Turnover</th>
                  <th>Fixed Assets</th>
                  <th>Company Size</th>
                  <th>Taxable Profit</th>
                  <th>CIT Rate</th>
                  <th>CIT Liability</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const t = row.totals_json || {};
                  return (
                    <tr key={row.id}>
                      <td>{row.status}</td>
                      <td>{new Date(row.created_at).toLocaleString()}</td>
                      <td>₦{Number(t.gross_turnover || 0).toLocaleString()}</td>
                      <td>₦{Number(t.fixed_assets || 0).toLocaleString()}</td>
                      <td>{t.company_size || "-"}</td>
                      <td>₦{Number(t.taxable_profit || 0).toLocaleString()}</td>
                      <td>{Number(t.cit_rate || 0) * 100}%</td>
                      <td>₦{Number(t.cit_liability || 0).toLocaleString()}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
